// deps
import { useCallback, useContext, useRef, useState } from "react";

// internal-deps
import {
    getBridgeSdk,
    DineoutCartContext,
    DineoutCartActionTypes,
    getDineoutFlowType,
    NEWRELIC_TABLES,
    recordNREvent,
    IDineoutCartResponseData,
    IDineoutCartResponse,
} from "@swiggy-private/checkout-core";
import { randomStringIdGenerator } from "@swiggy-private/core-ui";
import { Err, Result } from "@swiggy-private/js-fp";

// apis
import { updateDineoutCartWrapper } from "../apis/Cart";

// interfaces
import { ILocalCart } from "@dineout-cart-client/interfaces/LocalCart";
import { IErrorMeta } from "@dineout-cart-client/interfaces";

// helpers
import { isEventReservationCart, isStealStoreCart } from "@dineout-cart-client/helpers/cartHelper";
import { prepareLocalCartFromReviewedCart } from "@dineout-cart-client/utils/TableReservationUtils";

// constants
import { ERROR_STATUS_CODES } from "@dineout-cart-client/constants";
import {
    prepareStealDealLocalCartFromReviewedCart,
    reconcileLocalCartItemsWithBE,
} from "@dineout-cart-client/helpers/StealDealCart";

export interface UpdateCartError {
    errorPayload?: {
        error: {
            statusCode?: number;
            statusMessage?: string;
            ctaAction?: string;
            ctaText?: string;
            data?: IDineoutCartResponseData;
        };
    };
}

interface UpdateCartState {
    loading: boolean;
    error?: UpdateCartError;
}

interface IProps {
    setErrorPayload?: (errorPayload: IErrorMeta) => void;
}

export const usePerformUpdateCart = (props?: IProps) => {
    const { setErrorPayload } = props || {};
    const apiCallRef = useRef(false);
    const { setCartState, cartType, setUniqueIdForCart, cartState } = useContext(DineoutCartContext);
    const [state, setState] = useState<UpdateCartState>({
        loading: false,
    });

    const updateCart = useCallback(
        async (params: ILocalCart): Promise<void> => {
            if (apiCallRef.current) {
                return;
            }

            try {
                setState({
                    loading: true,
                });
                apiCallRef.current = true;

                // Make the API call to update cart
                const response = await updateDineoutCartWrapper(cartType, params);
                if (response?.isErr()) {
                    throw response;
                }

                try {
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                        type: "UPDATE_CART_SUCCESS",
                        cartType: cartType ?? "",
                        payload: JSON.stringify(params),
                        response: JSON.stringify(response.unwrap()),
                    });
                } catch (error) {
                    /* No-op*/
                }

                const cartResponse = response.unwrap();

                // this we need to do for steal deal cart to ensure that the local cart is updated with the latest quantities
                // because BE might have updated the quantities which is not equal to param which we send in 106 status code response
                const localCartForState = isStealStoreCart(cartType)
                    ? reconcileLocalCartItemsWithBE(params, cartResponse)
                    : params;

                setCartState({
                    type: DineoutCartActionTypes.UPDATE_LOCAL_AND_REVIEWED_CART,
                    payload: {
                        localCart: localCartForState,
                        reviewedCart: cartResponse,
                    },
                });

                // Generate new unique ID for cart updates this is use to avoid
                // the duplicate serviceline api calls
                const newUniqueId = randomStringIdGenerator();
                setUniqueIdForCart(newUniqueId);

                apiCallRef.current = false;
                setState({
                    loading: false,
                });

                //TODO this check to be removed when natie side changes were done
                if (isEventReservationCart(cartType) || isStealStoreCart(cartType)) {
                    /* Flow type is required for the dineout cart bridge functions, so that app can identify which cart flow is being used e.g. table reservation, event booking or bill payment */
                    /* If we don't pass the flow type, the bridge functions will not be able to identify which cart flow to update/delete
                    /* and this might create issues when the user is using multiple carts in the same app session */
                    const flowType = getDineoutFlowType(cartType);

                    const preparedLocalCart = isStealStoreCart(cartType)
                        ? prepareStealDealLocalCartFromReviewedCart(flowType, localCartForState, cartResponse)
                        : prepareLocalCartFromReviewedCart(flowType, params, cartResponse.data);

                    getBridgeSdk()
                        .dineoutUpdateLocalCart(preparedLocalCart)
                        .catch(() => void 0);
                }
            } catch (err) {
                const parsedError = Err(err) as Result<IDineoutCartResponse, UpdateCartError["errorPayload"]>;
                apiCallRef.current = false;
                const errorObj: UpdateCartError = {
                    errorPayload: parsedError.err().unwrap(),
                };

                recordNREvent({
                    eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                    type: "UPDATE_CART_FAILURE",
                    errorName: err instanceof Error ? err.name : "Unknown",
                    errorMessage: err instanceof Error ? err.message : String(err),
                });
                const errorCode = errorObj?.errorPayload?.error?.statusCode;
                if (errorCode === ERROR_STATUS_CODES.QR_INVALID) {
                    // TODOs: need to replace this with setSoft error or added in context
                    setErrorPayload?.(errorObj.errorPayload?.error as IErrorMeta);
                }

                // this we need to do cleanup in case of non-zero statuscode
                if (isStealStoreCart(cartType)) {
                    const preparedLocalCart = {
                        ...cartState?.localCart,
                        flowType: getDineoutFlowType(cartType),
                        latestCartCallTimestamp: 0,
                        latestCartResponse: undefined,
                    };

                    getBridgeSdk()
                        .dineoutUpdateLocalCart(preparedLocalCart)
                        .catch(() => void 0);
                }

                setState({
                    loading: false,
                    error: errorObj,
                });
            }
        },
        [cartType, setCartState, setErrorPayload, setUniqueIdForCart, cartState?.localCart],
    );

    return {
        updateCart,
        loading: state.loading,
        error: state.error,
    };
};
